import {
  Component,
  OnDestroy,
  OnInit,
  DOCUMENT,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FileEntity,
  FileTypes,
  Hub,
  HubAddress,
  HubContact,
  HubContactType,
  HubSettingType,
  HubType,
  ModalOptions,
  SocialNetworkInstance,
  SocialNetworkInstanceImageType,
  SocialNetworkInstancePrivacyType,
} from '@meplis/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalComponent } from '../../common/modal/modal.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ModalStep,
  ModalStepsComponent,
} from '../../common/modal-steps/modal-steps.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { QuillModule, QuillModules } from 'ngx-quill';
import { quillFormatsFromToolbar } from '../../../helpers/quill.helper';
import { QuillHtmlPasteDirective } from '../../../directives/quill-html-paste.directive';
import {
  PlacesAutocompleteDirective,
  PlacePickedEvent,
} from '../../../directives/places-autocomplete/places-autocomplete.directive';
import { FileCropperComponent } from '../../common/file/file-cropper/file-cropper.component';
import { ApiSocialV2Service } from '@cnp/services';
import { ToastService } from '@meplis/services';
import { SocialService } from '../../../services/application/social.service';
import { SocialNetworkInstanceImage } from 'libs/meplis/src/lib/common/models/social-network/social-network-instance-image';
import { Subscription } from 'rxjs';
import { PolicyType } from '@cnp/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { VendorStylesheetHelper } from '../../../helpers/vendor-stylesheet.helper';
import { FieldErrorComponent } from '../../common/field-error/field-error.component';

@Component({
  selector: 'app-hub-create-modal',
  imports: [
    FieldErrorComponent,
    TranslatePipe,
    ModalComponent,
    ModalStepsComponent,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatSelectModule,
    QuillModule,
    QuillHtmlPasteDirective,
    PlacesAutocompleteDirective,
    FileCropperComponent,
    FaIconComponent,
  ],
  templateUrl: './hub-create-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './hub-create-modal.component.scss',
})
export class HubCreateModalComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<HubCreateModalComponent>>(MatDialogRef);
  data = inject<{
    hubToEdit?: Hub;
  }>(MAT_DIALOG_DATA);
  private _translateService = inject(TranslateService);
  private _formBuilder = inject(FormBuilder);
  private _apiSocialV2 = inject(ApiSocialV2Service);
  private _toastService = inject(ToastService);
  private _socialService = inject(SocialService);
  private _document = inject<Document>(DOCUMENT);

  public modalOptions: ModalOptions;
  public formGroup: FormGroup;

  public selectedTabIndex = 0;

  /** Drives the left rail. Same order and labels the tabs used. */
  public readonly steps: ModalStep[] = [
    { label: 'HUBS.STEP_DETAILS' },
    { label: 'HUBS.STEP_SETTINGS' },
    { label: 'HUBS.STEP_LOCATION' },
    { label: 'HUBS.STEP_MEDIA' },
  ];

  /** How far the form has been let through; editing starts fully unlocked. */
  public furthestStepIndex = 0;

  public isSaving = false;
  public isEditMode = false;

  public hub: Hub = new Hub();
  public contacts: HubContact[] = [];
  public policyContent = '';
  public termsContent = '';

  public fileTypes = FileTypes;
  public imageTypes = SocialNetworkInstanceImageType;
  public privacyTypes = SocialNetworkInstancePrivacyType;
  public hubTypes = Object.values(HubType);
  public contactTypes = [HubContactType.EMAIL, HubContactType.PHONE];
  public readonly HubContactType = HubContactType;

  public richTextModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: [1, 2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link'],
      ['clean'],
    ],
    clipboard: { matchVisual: false },
  };

  /** Only what the toolbar above offers survives a paste. */
  public richTextFormats: string[] = quillFormatsFromToolbar(
    this.richTextModules.toolbar,
  );

  public settingKeys: { key: string; type: HubSettingType; label: string }[] = [
    {
      key: 'settingShowFeed',
      type: HubSettingType.SHOW_FEED,
      label: 'HUBS.SETTING_SHOW_FEED',
    },
    {
      key: 'settingShowCourse',
      type: HubSettingType.SHOW_COURSE,
      label: 'HUBS.SETTING_SHOW_COURSE',
    },
    {
      key: 'settingShowVideo',
      type: HubSettingType.SHOW_VIDEO,
      label: 'HUBS.SETTING_SHOW_VIDEO',
    },
    {
      key: 'settingShowProgram',
      type: HubSettingType.SHOW_PROGRAMS,
      label: 'HUBS.SETTING_SHOW_PROGRAM',
    },
    {
      key: 'settingShowEvent',
      type: HubSettingType.SHOW_EVENTS,
      label: 'HUBS.SETTING_SHOW_EVENT',
    },
    {
      key: 'settingShowGroup',
      type: HubSettingType.SHOW_GROUPS,
      label: 'HUBS.SETTING_SHOW_GROUP',
    },
    {
      key: 'settingShowCase',
      type: HubSettingType.SHOW_CLINICAL_CASES,
      label: 'HUBS.SETTING_SHOW_CASE',
    },
    {
      key: 'settingShowMember',
      type: HubSettingType.SHOW_MEMBERS,
      label: 'HUBS.SETTING_SHOW_MEMBER',
    },
    {
      key: 'settingShowAbout',
      type: HubSettingType.SHOW_ABOUT,
      label: 'HUBS.SETTING_SHOW_ABOUT',
    },
  ];

  private _userInstanceId: string;
  private _userInstanceSub: Subscription;
  private _saveSub: Subscription;
  private _editSub: Subscription;
  private _policiesSub: Subscription;

  constructor() {
    VendorStylesheetHelper.ensureLoaded(
      this._document,
      '/vendor/quill.snow.css',
    );
    const settingDefaults: Record<string, boolean> = {};
    this.settingKeys.forEach((s) => (settingDefaults[s.key] = true));

    this.formGroup = this._formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      about: [''],
      privacy: [SocialNetworkInstancePrivacyType.PUBLIC, Validators.required],
      hubType: [null, Validators.required],
      campusSlug: [''],
      tenantId: [''],
      hasCourse: [false],
      hasCaremonitor: [false],
      hasNewCase: [false],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      country: [''],
      locationLat: [null],
      locationLong: [null],
      location: [''],
      ...settingDefaults,
    });

    this.modalOptions = {
      title: this._translateService.instant('HUBS.NEW'),
      positiveButtonLabel: this._translateService.instant('NEXT'),
      negativeButtonLabel: this._translateService.instant('CANCEL'),
      showPositiveButton: true,
      showNegativeButton: true,
      closeable: true,
      surface: 'canvas',
      layout: 'stepped',
    };
  }

  public ngOnInit(): void {
    // Ensure instance exists for image management
    if (!this.hub.instance) {
      this.hub.instance = new SocialNetworkInstance();
    }

    this._userInstanceSub = this._socialService
      .getUserInstance()
      .subscribe((instance) => {
        this._userInstanceId = instance?.token;
      });

    if (this.data?.hubToEdit) {
      this.isEditMode = true;
      this.modalOptions.title = this._translateService.instant('HUBS.EDIT_HUB');
      // An existing hub already satisfied every step, so editing should not
      // make someone walk the wizard again to reach the one field they came for.
      this.furthestStepIndex = this.steps.length - 1;
      // Delay so Quill editors are mounted before patchValue — otherwise they
      // display raw HTML instead of rendering it.
      setTimeout(() => this._mapHubToForm(), 50);
      this._loadHubPolicies();
    }
  }

  public ngOnDestroy(): void {
    this._userInstanceSub?.unsubscribe();
    this._saveSub?.unsubscribe();
    this._editSub?.unsubscribe();
    this._policiesSub?.unsubscribe();
  }

  private _loadHubPolicies(): void {
    const h = this.data.hubToEdit;
    if (!h.instance?.token || !h.token) return;

    this._policiesSub = this._apiSocialV2
      .getHubPolicies(h.instance.token, h.token)
      .subscribe({
        next: (policies) => {
          for (const policy of policies) {
            if (policy.type === PolicyType.HUB_POLICY)
              this.policyContent = policy.value || '';
            if (policy.type === PolicyType.HUB_TERMS)
              this.termsContent = policy.value || '';
          }
        },
        error: () => {},
      });
  }

  private _mapHubToForm(): void {
    const h = this.data.hubToEdit;
    this.hub = { ...h };
    if (!this.hub.instance) this.hub.instance = new SocialNetworkInstance();

    this.formGroup.patchValue({
      name: h.name || '',
      description: h.description || '',
      about: (h as any).about || '',
      privacy: h.privacy || SocialNetworkInstancePrivacyType.PUBLIC,
      hubType: h.hubType || (h as any).type || null,
      campusSlug: h.campusSlug || '',
      tenantId: h.tenantId || '',
      address: h.address?.address || '',
      city: h.address?.city || '',
      state: h.address?.state || '',
      zip: h.address?.zip || '',
      country: h.address?.country || '',
      locationLat: h.address?.locationLat ?? null,
      locationLong: h.address?.locationLong ?? null,
      location: this._formatAddress(h.address),
    });

    this.formGroup
      .get('hasCourse')
      ?.setValue(h.hasCourse === true || (h as any).hasCourse === 'true');
    this.formGroup
      .get('hasCaremonitor')
      ?.setValue(
        h.hasCaremonitor === true || (h as any).hasCaremonitor === 'true',
      );
    this.formGroup
      .get('hasNewCase')
      ?.setValue(h.hasNewCase === true || (h as any).hasNewCase === 'true');

    if (h.settings?.length) {
      h.settings.forEach((s) => {
        const entry = this.settingKeys.find((sk) => sk.type === s.type);
        if (entry) {
          const val = s.value;
          this.formGroup
            .get(entry.key)
            ?.setValue(val === true || val === 'true');
        } else if (s.type === HubSettingType.HUB_POLICY) {
          this.policyContent = (s.value as string) || '';
        } else if (s.type === HubSettingType.HUB_TERMS) {
          this.termsContent = (s.value as string) || '';
        }
      });
    }

    this.contacts = h.contacts ? [...h.contacts] : [];

    // Sync instance images for the file croppers
    this.hub.instance.images = h.instance?.images ? [...h.instance.images] : [];
  }

  private _mapFormToHub(): void {
    const v = this.formGroup.value;

    this.hub.name = v.name;
    this.hub.description = v.description;
    (this.hub as any).about = v.about || undefined;
    this.hub.privacy = v.privacy;
    // Map to the API field name 'type' (backend expects HubType here)
    (this.hub as any).type = v.hubType;
    this.hub.campusSlug = v.campusSlug || undefined;
    this.hub.tenantId = v.tenantId || undefined;
    this.hub.hasCourse = v.hasCourse;
    this.hub.hasCaremonitor = v.hasCaremonitor;
    this.hub.hasNewCase = v.hasNewCase;

    const hasAddress = v.address || v.city || v.state || v.zip || v.country;
    this.hub.address = hasAddress
      ? ({
          address: v.address || undefined,
          city: v.city || undefined,
          state: v.state || undefined,
          zip: v.zip || undefined,
          country: v.country || undefined,
          locationLat: v.locationLat ?? undefined,
          locationLong: v.locationLong ?? undefined,
        } as HubAddress)
      : undefined;

    // Show/hide settings — value is boolean as the API expects
    this.hub.settings = this.settingKeys.map((sk) => ({
      type: sk.type,
      value: v[sk.key] ?? true,
    }));

    // Policy and terms as settings
    if (this.policyContent) {
      this.hub.settings.push({
        type: HubSettingType.HUB_POLICY,
        value: this.policyContent,
      });
    }
    if (this.termsContent) {
      this.hub.settings.push({
        type: HubSettingType.HUB_TERMS,
        value: this.termsContent,
      });
    }

    this.hub.contacts = this.contacts.filter((c) => c.type && c.value);
  }

  private _formatAddress(address?: HubAddress): string {
    return [
      address?.address,
      address?.city,
      address?.state,
      address?.zip,
      address?.country,
    ]
      .filter(Boolean)
      .join(', ');
  }

  public onPlacePicked(event: PlacePickedEvent): void {
    this.formGroup.patchValue({
      location: event.address,
      address: event.streetAddress ?? event.address,
      city: event.city ?? '',
      state: event.state ?? '',
      zip: event.zip ?? '',
      country: event.country ?? '',
      locationLat: event.lat,
      locationLong: event.lng,
    });
  }

  public onLocationInput(value: string): void {
    this.formGroup.patchValue({
      address: value,
      city: '',
      state: '',
      zip: '',
      country: '',
      locationLat: null,
      locationLong: null,
    });
  }

  public scrollFieldIntoView(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    const isField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.classList.contains('ql-editor');
    if (!isField) return;
    const group = target.closest('.field__group, .field__row') as HTMLElement;
    if (group) {
      group.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public getFormControl(name: string): FormControl {
    return this.formGroup.get(name) as FormControl;
  }

  // Images stored in hub.instance.images to match the API payload structure
  public confirmFile(
    file: FileEntity,
    type: SocialNetworkInstanceImageType,
  ): void {
    const image: SocialNetworkInstanceImage = {
      fileHash: file.fileHash,
      fileId: file.fileId,
      file,
      type,
    };
    if (!this.hub.instance.images) this.hub.instance.images = [];
    this.hub.instance.images = this.hub.instance.images.filter(
      (img) => img.type !== type,
    );
    this.hub.instance.images.push(image);
  }

  public removeFile(fileId: number): void {
    this.hub.instance.images = this.hub.instance.images?.filter(
      (img) => img.fileId !== fileId,
    );
  }

  public getImage(
    type: SocialNetworkInstanceImageType,
  ): FileEntity | undefined {
    return this.hub.instance?.images?.find((img) => img.type === type)?.file;
  }

  // Contacts
  public addContact(): void {
    this.contacts.push({ type: HubContactType.EMAIL, value: '' });
  }

  public removeContact(index: number): void {
    this.contacts.splice(index, 1);
  }

  // Tab navigation
  public onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
    this.furthestStepIndex = Math.max(this.furthestStepIndex, index);
    // A crop belongs to the step that opened it.
    this._updateButtons();
  }

  public onPositiveClick(): void {
    const lastTab = 3;

    if (this.selectedTabIndex < lastTab) {
      if (this.selectedTabIndex === 0 && !this._isDetailsValid()) {
        this.formGroup.markAllAsTouched();
        return;
      }
      this.selectedTabIndex++;
      this._updateButtons();
      return;
    }

    if (this.formGroup.invalid) {
      this.selectedTabIndex = 0;
      this.formGroup.markAllAsTouched();
      return;
    }

    this._saveOrEdit();
  }

  public onNegativeClick(): void {
    if (this.selectedTabIndex > 0) {
      this.selectedTabIndex--;
      this._updateButtons();
      return;
    }
    this.dialogRef.close();
  }

  public isPositiveDisabled(): boolean {
    return this.isSaving;
  }

  private _isDetailsValid(): boolean {
    return ['name', 'description', 'hubType', 'privacy'].every(
      (ctrl) => this.formGroup.get(ctrl)?.valid,
    );
  }

  private _updateButtons(): void {
    const isLast = this.selectedTabIndex === 3;
    this.modalOptions = {
      ...this.modalOptions,
      positiveButtonLabel: this._translateService.instant(
        isLast ? 'PUBLISH' : 'NEXT',
      ),
      negativeButtonLabel: this._translateService.instant(
        this.selectedTabIndex === 0 ? 'CANCEL' : 'PREVIOUS',
      ),
    };
  }

  private _saveOrEdit(): void {
    this._mapFormToHub();
    if (this.isEditMode) {
      this._edit();
    } else {
      this._save();
    }
  }

  private _save(): void {
    this.isSaving = true;
    this._saveSub = this._apiSocialV2
      .saveHub(this.hub, this._userInstanceId)
      .subscribe({
        next: (hub) => {
          this.isSaving = false;
          this._toastService.showSuccess('HUBS.CREATED');
          this.dialogRef.close(hub);
        },
        error: (error) => {
          this.isSaving = false;
          this._toastService.showError(error);
        },
      });
  }

  private _edit(): void {
    this.isSaving = true;
    this._editSub = this._apiSocialV2
      .editHub(this.hub, this.hub.instance?.token, this.hub.token)
      .subscribe({
        next: (hub) => {
          this.isSaving = false;
          this._toastService.showSuccess('HUBS.UPDATED');
          this.dialogRef.close(hub ?? true);
        },
        error: (error) => {
          this.isSaving = false;
          this._toastService.showError(error);
        },
      });
  }
}
